import { useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { ACCESS_DENIED_EVENT } from '@/security/accessDeniedHook';

/**
 * ITER283 Phase 4 — Global listener that displays a humanised, localised
 * toast whenever the backend AccessGuard refuses a module (HTTP 423).
 *
 * Mounted once at the app root (see App.js). Debounces bursts so a
 * hammered endpoint (e.g. polling that hits a locked route) does not
 * spam the UI — only one toast per (module, status) combo every 4s.
 */
export default function AccessDeniedListener() {
  const { t } = useTranslation();
  const lastRef = useRef({});   // key: "module|status" → timestamp

  useEffect(() => {
    function onDenied(ev) {
      const detail = ev?.detail || {};
      const {
        message_key,
        message,
        status,
        module,
        support_email,
        support_mailto,
      } = detail;
      const key = `${module || 'unknown'}|${status || 'unknown'}`;
      const now = Date.now();
      if ((lastRef.current[key] || 0) > now - 4000) return;
      lastRef.current[key] = now;

      const supportEmail = support_email
        || t('access_guard.support_email_default', {
              defaultValue: 'contact@japapmessenger.com' });
      const mailto = support_mailto || `mailto:${supportEmail}`;
      const i18nKey = message_key || 'access_guard.default_denied';
      // t() falls back to the backend-provided message if the key is
      // not translated yet in the active language.
      const humanMsg = t(i18nKey, {
        support_email: supportEmail,
        defaultValue: message
          || t('access_guard.default_denied',
                { support_email: supportEmail,
                  defaultValue: 'This feature is temporarily unavailable.' }),
      });
      const title = t('access_guard.toast_title',
                       { defaultValue: 'Feature unavailable' });
      const supportLabel = t('access_guard.support_button',
                              { defaultValue: 'Contact support' });

      toast(title, {
        description: humanMsg,
        duration: 8000,
        action: {
          label: supportLabel,
          onClick: () => { window.location.href = mailto; },
        },
        id: `access-denied-${key}`,
      });
    }
    window.addEventListener(ACCESS_DENIED_EVENT, onDenied);
    return () => window.removeEventListener(ACCESS_DENIED_EVENT, onDenied);
  }, [t]);

  return null;
}
